import React, { useState, useLayoutEffect } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Divider,
  Popconfirm,
  Space,
  Input,
  Select,
} from "antd";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  capitalizeWords,
  getPayTypeLabel,
  getRoomCapicityLabel,
  getRoomTypeLabel,
  getSegmentLabel,
  getServiceLabel,
  PAYMENT_METHOD,
} from "../../constants/constent";
import { displayMessage, postAPI } from "../../utils/common";
import {
  BOOKING_ACTION,
  BOOKING_ADV_PAY,
  BOOKING_ADV_PAY_VERIFY,
  BOOKING_ASSIGN_UNASSIGN,
  BOOKING_CHECKIN,
  BOOKING_INVOICE,
  BOOKING_QR_PASS,
  BOOKING_ROOMS,
  IMAGE_BASE_URL,
} from "../../constants/api";
import {
  ERROR_MSG_TYPE,
  SUCCESS_MESSAGE,
  SUCCESS_MSG_TYPE,
} from "../../constants/messages";
import BookingOrderDrawer from "./BookingOrderDrawer";
import CheckoutModal from "./CheckoutModal";
import { useNavigate } from "react-router-dom";
import BookingEditModal from "./BookingEditModal";
import BookingLogsModal from "./BookingLogsModal";
import ExtraServiceModal from "./extra_service/ExtraServiceModal";
import BookingAppliedServiceModal from "./BookingAppliedServiceModal";
import LS_SERVICE from "../../utils/localStorage";
import AdvancePaymentModal from "./AdvancePaymentModal";

const BookingPopOver = ({ visible, onClose, booking }) => {
  // Hooks should be called at the top of the component
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groupedRooms, setGroupedRooms] = useState({}); // To store rooms grouped by category
  const [roomSelectionVisible, setRoomSelectionVisible] = useState(false); // State for room selection modal
  // Booking Cancel
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const {
    id,
    customer_name,
    room_category_title,
    user,
    room,
    order_id,
    multi_book_id,
    room_id,
    room_type,
    total_days,
    checkin_date,
    checkout_date,
    booking_segment,
    booking_source,
    payment_method,
    total_with_gst,
    bill_amount,
    booking_status,
    payment_status,
    advance_amount,
    order_amount,
    ex_service_amount,
    rate_plans,
  } = booking;

  const formattedCheckIn = moment(checkin_date).format("D MMM YY");
  const formattedCheckOut = moment(checkout_date).format("D MMM YY");

  const getData = () => {
    setLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        groupRoomsByCategory(data["data"]["rooms"]);
        setLoading(false);
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setLoading(false);
      }
    };
    const errorFn = function () {
      setLoading(false);
    };
    postAPI(
      BOOKING_ROOMS,
      {
        start_date: formattedCheckIn,
        end_date: formattedCheckOut,
      },
      successFn,
      errorFn,
    );
  };

  const groupRoomsByCategory = (rooms) => {
    const grouped = rooms.reduce((acc, room) => {
      const category = room.room_category?.title || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(room, ...room.similar_rooms);
      return acc;
    }, {});
    setGroupedRooms(grouped);
  };

  useLayoutEffect(() => {
    getData();
  }, [formattedCheckIn, formattedCheckOut]);

  const assignUnassign = async (type, room_id = null) => {
    try {
      setLoading(true);
      const successFn = function (data) {
        if (data.status === true) {
          setLoading(false);
          onClose();
          displayMessage(SUCCESS_MSG_TYPE, data.message);
        } else {
          displayMessage(ERROR_MSG_TYPE, data.message);
          setLoading(false);
        }
      };
      const errorFn = function () {
        setLoading(false);
      };

      postAPI(
        BOOKING_ASSIGN_UNASSIGN,
        {
          booking_id: id,
          type: type,
          room_id: room_id,
        },
        successFn,
        errorFn,
      );
    } catch (err) {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Failed to assignUnassign" + err);
    }
  };

  // Handle Room Assignment
  const handleAssign = (roomId) => {
    assignUnassign("assign", roomId);
    setRoomSelectionVisible(false); // Close room selection modal
  };

  // Handle Room Unassignment
  const handleUnassign = () => {
    assignUnassign("unassign"); // Unassign the room by passing null
  };

  const bookingAction = (actionType) => {
    setLoading(true);
    let body = {
      id: id,
      action_type: actionType,
      reason: cancelReason ?? "",
      cancel_by: LS_SERVICE.get("user_type") ?? "",
    };

    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);
        onClose();
        if (actionType === "reject") {
          setIsCancelModalVisible(false);
          setCancelReason("");
        }
        displayMessage(SUCCESS_MESSAGE, data.message);
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to action");
    };
    postAPI(BOOKING_ACTION, body, successFn, errorFn);
  };

  const checkin = () => {
    setLoading(true);
    let body = {
      booking_id: id,
    };
    console.log(body);
    const successFn = function (data) {
      setLoading(false);
      displayMessage(
        data.status ? SUCCESS_MESSAGE : ERROR_MSG_TYPE,
        data.message,
      );
      onClose();
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Check-In");
    };
    postAPI(BOOKING_CHECKIN, body, successFn, errorFn);
  };

  // Booking Checkout
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleClose = () => {
    setEditBookingVisible(false);
    setIsModalVisible(false);
    setLogBookingVisible(false);
    setIsServiceModal(false);
    onClose();
    getData();
  };
  // Booking Checkout
  const openModel = () => {
    setIsModalVisible(true);
  };

  // Booking Order  View
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  // Booking Detail View
  const openOrderDrawer = () => {
    setIsOrderDrawerOpen(true);
  };

  const closeOrderDrawer = () => {
    setIsOrderDrawerOpen(false);
  };

  // Advance Amount
  const [advModalVisible, setAdvModalVisible] = useState(false);
  const [forceCheckIn, setForceCheckIn] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const handleModalOpen = (force_check_in) => {
    setAdvModalVisible(true);
    setForceCheckIn(force_check_in);
  };

  const handleModalClose = () => {
    setAdvModalVisible(false);
    setForceCheckIn(false);
  };

  const handleAdvancePaySubmit = () => {
    if (parseFloat(advanceAmount) <= 0) {
      displayMessage(
        ERROR_MSG_TYPE,
        "Please enter a valid amount for advance payment",
      );
      return;
    }
    adv_pay_init(id, advanceAmount);
    handleModalClose(); // Close the modal after submitting
  };

  const adv_pay_init = (id, advAmount) => {
    setLoading(true);
    let body = {
      id: id,
      total_amount: advAmount,
      payment_method: paymentMethod,
      force_check_in: forceCheckIn ? "yes" : "no",
    };
    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);

        if (data.payment_method === "razorpay") {
          const options = {
            key: data.options.key,
            amount: data.options.amount * 100,
            currency: data.options.currency,
            name: data.options.name,
            description: data.options.description,
            image: IMAGE_BASE_URL + data.options.image,
            order_id: data.options.order_id,
            prefill: {
              name: data.options.prefill.name,
              email: data.options.prefill.email,
              contact: data.options.prefill.contact,
            },
            handler: function (response) {
              let body = {
                ...response,
                booking_id: id,
                total_amount: advAmount,
                force_check_in: data.force_check_in,
              };
              console.log(body);
              const successFn = function (data) {
                displayMessage(SUCCESS_MESSAGE, data.message);
                setAdvanceAmount("");
                onClose();
              };
              const errorFn = function () {
                setLoading(false);
                displayMessage(
                  ERROR_MSG_TYPE,
                  "Unable to do payment verification",
                );
              };
              postAPI(BOOKING_ADV_PAY_VERIFY, body, successFn, errorFn);
            },
            theme: {
              color: "#F37254",
            },
          };
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          displayMessage(SUCCESS_MESSAGE, data.message);
          setAdvanceAmount("");
          onClose();
        }
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };

    // Error callback
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to do payment");
    };

    // Make API call to your backend to initiate Razorpay
    postAPI(BOOKING_ADV_PAY, body, successFn, errorFn);
  };

  // Edit Booking Modal
  const [editBookingVisible, setEditBookingVisible] = useState(false);

  // Booking Logs Modal
  const [logBookingVisible, setLogBookingVisible] = useState(false);

  // Booking Extra Service Modal
  const [isServiceModal, setIsServiceModal] = useState(false);

  // Booking Extra Services Modal
  const [isServicesModal, setIsServicesModal] = useState(false);
  return (
    <>
      {/* Main Modal */}
      <Modal
        title={null}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
      >
        <Row
          style={{
            background: "var(--primary-color)",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          {/* LEFT: Guest Info */}
          <Col span={18}>
            <div
              style={{
                background: "var(--primary-color)",
                color: "#fff",
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div>
                  <UserOutlined /> <b>{customer_name + " - " + id}</b>
                </div>
                <div>
                  <MailOutlined /> {user.email}
                </div>
              </div>
              <Divider style={{ borderColor: "#ccc", margin: "10px 0" }} />
              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <b>Check-in</b> : {formattedCheckIn}
                  </p>
                  <p>
                    <b>Segment</b> :{" "}
                    {capitalizeWords(
                      getSegmentLabel(booking_segment) +
                        " (" +
                        booking_source.toUpperCase() +
                        ")",
                    )}
                  </p>
                  <p>
                    <b>Room</b> :{" "}
                    {/* {getRoomTypeLabel(rate_plans?.room_type) || "-"} */}
                    {room_category_title}
                  </p>

                  <p>
                    <b>No. of guest</b> : {getRoomCapicityLabel(room_type)}
                  </p>
                  <p>
                    <b>No. of Nights</b> : {total_days}
                  </p>
                  <p>
                    <b>Payment Mode</b> :{" "}
                    {getPayTypeLabel(payment_method) || "NA"}
                  </p>
                  <p>
                    <b>Advance Amount</b> : {advance_amount || "NA"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <b>Check-out</b> : {formattedCheckOut}
                  </p>
                  <p>
                    <b>Booking-ID</b> : {order_id || "-"}
                  </p>
                  <p>
                    <b>Multi-Booking-ID</b> : {multi_book_id || "-"}
                  </p>
                  <p>
                    <b>Room Service</b> :{" "}
                    {getServiceLabel(rate_plans?.room_service) || "-"}
                  </p>
                </Col>
              </Row>
            </div>
            <div
              style={{
                background: "#003f5c",
                color: "#fff",
                padding: "8px 16px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <b>Amount</b> :{" "}
                {parseFloat(total_with_gst) +
                  parseFloat(order_amount ?? 0) +
                  parseFloat(ex_service_amount ?? 0)}
              </div>
              <div>
                <b>Balance</b> : {parseFloat(bill_amount).toFixed(2) ?? 0}
              </div>
            </div>
          </Col>

          {/* RIGHT: Action Buttons */}
          <Col span={6} style={{ background: "#ffffff", padding: 10 }}>
            {booking_status !== "3" ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Button block onClick={() => setLogBookingVisible(true)}>
                  Logs
                </Button>

                {booking_status !== "2" ? (
                  <Button block onClick={() => setIsServiceModal(true)}>
                    Add Service
                  </Button>
                ) : null}
                {booking_status !== "2" ? (
                  <Button block onClick={() => setIsServicesModal(true)}>
                    Services
                  </Button>
                ) : null}

                {booking_status !== "3" ? (
                  <Button block onClick={() => setEditBookingVisible(true)}>
                    Edit Booking
                  </Button>
                ) : null}
                {booking_status === "2" && payment_status === "1" ? (
                  <>
                    <Button
                      onClick={(e) => {
                        const encodedId = btoa(id.toString()); // Convert the ID to Base64
                        const url = `${BOOKING_INVOICE}${encodedId}`;
                        window.open(url, "_blank"); // Open the URL in a new tab or window
                      }}
                      loading={loading}
                      disabled={loading}
                      block
                    >
                      INVOICE
                    </Button>
                  </>
                ) : null}
                {booking_status !== "2" ? (
                  <Button block onClick={() => setRoomSelectionVisible(true)}>
                    {!room_id ? `Assign` : `Change Room`}
                  </Button>
                ) : null}
                {booking_status !== "2" && room_id ? (
                  <Button block onClick={() => handleUnassign()}>
                    Unassign
                  </Button>
                ) : null}

                {booking_status === "0" ? (
                  <Button block onClick={() => bookingAction("accept")}>
                    Accept
                  </Button>
                ) : null}
                {booking_status === "0" || booking_status === "4" ? (
                  <Button
                    loading={loading}
                    disabled={loading}
                    danger
                    block
                    onClick={() => {
                      setIsCancelModalVisible(true);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
                {booking_status === "4" ? (
                  <Button
                    block
                    onClick={(e) =>
                      advance_amount === null ||
                      parseInt(advance_amount) === 0.0
                        ? handleModalOpen(true)
                        : checkin()
                    }
                  >
                    Check-In
                  </Button>
                ) : null}
                <Button
                  block
                  onClick={(e) => {
                    const encodedId = btoa(id.toString());
                    const url = `${BOOKING_QR_PASS}${encodedId}`;
                    window.open(url, "_blank");
                  }}
                >
                  QR Pass
                </Button>
                {booking_status !== "0" &&
                booking_status !== "3" &&
                booking_status !== "2" ? (
                  <Button block onClick={(e) => handleModalOpen(false)}>
                    Advance AMT
                  </Button>
                ) : null}
                {booking_status === "1" ? (
                  <Popconfirm
                    title="Checkout the booking"
                    description="Are you sure to checkout?"
                    onConfirm={() => openModel()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button loading={loading} disabled={loading} block>
                      Checkout
                    </Button>
                  </Popconfirm>
                ) : null}
                {booking_status === "1" ? (
                  <>
                    <Button
                      loading={loading}
                      disabled={loading}
                      onClick={openOrderDrawer}
                      danger
                    >
                      Orders
                    </Button>
                    <Button
                      onClick={(e) => navigate("/order/product/" + id)}
                      loading={loading}
                      disabled={loading}
                      block
                    >
                      Take Order
                    </Button>
                  </>
                ) : null}
              </div>
            ) : (
              <Button block onClick={() => setLogBookingVisible(true)}>
                Logs
              </Button>
            )}
          </Col>
        </Row>
      </Modal>

      {/* Room Selection Modal for Assigning Room */}
      <Modal
        title=""
        visible={roomSelectionVisible}
        onCancel={() => setRoomSelectionVisible(false)}
        footer={null}
        width={500}
      >
        {Object.keys(groupedRooms).map((category) => (
          <div key={category}>
            <h3>
              <b>{category}</b>
            </h3>
            <Row gutter={16}>
              {groupedRooms[category].map((room) => (
                <Col span={12} key={room.id}>
                  <Button
                    block
                    onClick={() => handleAssign(room.id)}
                    disabled={loading}
                  >
                    {room.name} ({room.room_no})
                  </Button>
                </Col>
              ))}
            </Row>
            <Divider />
          </div>
        ))}
      </Modal>

      {/* Cancel Booking Modal */}
      <Modal
        title="Cancel the booking"
        visible={isCancelModalVisible}
        onOk={() => {
          bookingAction("reject");
        }}
        onCancel={() => setIsCancelModalVisible(false)}
        okText="Confirm Cancel"
        cancelText="Close"
      >
        <p>Please provide a reason for cancellation:</p>
        <Input.TextArea
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Enter cancellation reason..."
        />
      </Modal>

      {/* Extra Service Modal */}
      {isServiceModal && id && (
        <ExtraServiceModal
          visible={isServiceModal}
          onClose={handleClose}
          booking_id={id}
        />
      )}

      {/* Booking Extra Service Modal */}
      {isServicesModal && id && (
        <BookingAppliedServiceModal
          visible={isServicesModal}
          onClose={() => {
            setIsServicesModal(false);
          }}
          booking_id={id}
        />
      )}

      {/* Checkout Modal */}
      {isModalVisible && booking && (
        <CheckoutModal
          visible={isModalVisible}
          onClose={handleClose}
          booking={booking} // Pass the booking data here
        />
      )}

      {/* Booking Order View */}
      {isOrderDrawerOpen && booking && (
        <BookingOrderDrawer
          open={isOrderDrawerOpen}
          onClose={closeOrderDrawer}
          booking={booking}
        />
      )}

      {/* Booking Logs Modal */}
      {logBookingVisible && id && (
        <BookingLogsModal
          visible={logBookingVisible}
          onClose={() => {
            setLogBookingVisible(false);
          }}
          booking_id={id}
        />
      )}

      {/* Booking Edit Modal */}
      {editBookingVisible && booking && (
        <BookingEditModal
          visible={editBookingVisible}
          onClose={handleClose}
          booking={booking}
        />
      )}

      {/* Advance Amount Modal */}
      {advModalVisible && (
        <AdvancePaymentModal
          visible={advModalVisible}
          onCancel={() => setAdvModalVisible(false)}
          onSubmit={handleAdvancePaySubmit}
          loading={loading}
          amount={advanceAmount}
          setAmount={setAdvanceAmount}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          PAYMENT_METHOD={PAYMENT_METHOD}
        />
      )}
    </>
  );
};

export default BookingPopOver;
